import { InputAdornment, Stack, Typography, useTheme } from '@mui/material';
import { FormTextFieldStyled } from '@schooly/components/form-text-field';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getPaletteColorFromName } from '../../theme/theme';
import { Spin } from '../Spin';
import { CellProps } from './cell.type';
import { ColorCellWrapper } from './CellWrappers';

const CELL_DEFAULT_WIDTH = 112;

export type ScoreCellProps = CellProps<number> & {
  scoreOutOf: number | null;
  getDisplayValue?: (val: number, scoreOutOf: number) => string;
};

export const ScoreCell: FC<ScoreCellProps> = ({
  value: initialValue,
  scoreOutOf,
  canEdit = true,
  status,
  backgroundColor,
  onUpdate,
  width = CELL_DEFAULT_WIDTH,
  focused,
  textColor,
  getDisplayValue,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { palette } = useTheme();
  const { isEdit, editValue, enterEditMode, exitEditMode, handleInputChange, value } = useEditMode(
    initialValue,
    focused,
    inputRef,
    onUpdate,
  );

  const isLoading = status === 'loading';
  const isError = status === 'error';
  const hasScoreOutOf = Boolean(scoreOutOf);
  const isEditOrError = isEdit || isError;

  const colors = useScoreCellColors(status, isEdit, textColor);

  const customDisplayValue = useMemo(() => {
    return getDisplayValue && scoreOutOf && typeof initialValue === 'number'
      ? getDisplayValue(initialValue, scoreOutOf)
      : null;
  }, [getDisplayValue, scoreOutOf, initialValue]);

  const displayValue = isEdit ? editValue : (customDisplayValue ?? value);
  const showCustomTextValue = !isEditOrError && customDisplayValue;
  const alignCenter = showCustomTextValue || !hasScoreOutOf;
  const showScoreOutOf = !alignCenter && (value || isEdit || isError);

  const scoreColorValue = getPaletteColorFromName(palette, colors.scoreColor);
  const scoreOutOfColorValue = getPaletteColorFromName(palette, colors.scoreOutOfColor);

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (canEdit) {
        enterEditMode();
      }
      if (focused) {
        e.stopPropagation();
      }
    },
    [canEdit, focused, enterEditMode],
  );

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      handleInputChange(event.target.value);
    },
    [handleInputChange],
  );

  return (
    <Stack
      onClick={handleClick}
      sx={{
        justifyContent: 'center',
        alignItems: 'center',
        width,
        height: '100%',
        position: 'relative',
        backgroundColor: isError ? 'error.superLight' : undefined,
      }}
    >
      {!isLoading && <ColorCellWrapper color={backgroundColor ?? null} error={isError} />}
      {isLoading && <Spin size={2} />}
      <FormTextFieldStyled
        variant="standard"
        ref={inputRef}
        onBlur={exitEditMode}
        onFocus={enterEditMode}
        onChange={handleChange}
        value={isError ? editValue : displayValue}
        placeholder={isEdit ? undefined : '-'}
        disabled={!canEdit}
        sx={({ palette, typography }) => ({
          display: isLoading ? 'none' : 'flex',

          '& .MuiInputBase-root::before, & .MuiInputBase-root::after': {
            display: 'none !important',
          },
          '& .MuiInputBase-root': {
            justifyContent: alignCenter ? 'center' : undefined,
          },
          '& .Mui-disabled': {
            color: `${scoreColorValue} !important`,
            WebkitTextFillColor: `${scoreColorValue} !important`,
            '& .color-score-out-of': {
              color: `${scoreOutOfColorValue} !important`,
              WebkitTextFillColor: `${scoreOutOfColorValue} !important`,
            },
            '&::placeholder': {
              color: `${palette.common.grey} !important`,
              WebkitTextFillColor: `${palette.common.grey} !important`,
            },
          },
          '& .MuiInput-input': {
            width: `calc(100% / 2)`,
            textAlign: 'right',
            ...typography.h3,
            height: '44px !important',
            margin: '0 !important',
            padding: '0 !important',
            color: `${scoreColorValue} !important`,
            WebkitTextFillColor: `${scoreColorValue} !important`,

            '&::placeholder': {
              color: `${palette.common.grey} !important`,
              WebkitTextFillColor: `${palette.common.grey} !important`,
              opacity: 1,
            },
          },
        })}
        inputProps={{ min: 0 }}
        InputProps={{
          sx: {
            '& input': {
              textAlign: alignCenter ? 'center !important' : 'right',
            },
          },
          endAdornment: showScoreOutOf ? (
            <InputAdornment className="color-score-out-of" position="end">
              <Stack justifyContent="flex-end" height={16}>
                <Typography
                  variant="caption"
                  sx={{ textAlign: 'left', ml: -0.75 }}
                  color={colors.scoreOutOfColor}
                >
                  /{scoreOutOf}
                </Typography>
              </Stack>
            </InputAdornment>
          ) : null,
        }}
      />
    </Stack>
  );
};

const useScoreCellColors = (
  status: ScoreCellProps['status'],
  isEdit: boolean,
  textColor?: [string, string | undefined],
) => {
  const isError = status === 'error';
  const [scoreColor, scoreOutOfColor] = textColor || [];

  return useMemo(() => {
    if (isError) {
      return {
        scoreColor: 'error.main',
        scoreOutOfColor: 'error.main',
        backgroundColor: 'error.superLight',
      };
    }

    if (isEdit) {
      return {
        scoreColor: 'primary.main',
        scoreOutOfColor: 'common.grey',
      };
    }

    return {
      scoreColor: scoreColor || 'primary.main',
      scoreOutOfColor: scoreOutOfColor || 'common.grey',
    };
  }, [isError, isEdit, scoreColor, scoreOutOfColor]);
};

const useEditMode = (
  initialValue: number | null,
  focused: boolean | undefined,
  inputRef: React.RefObject<HTMLInputElement>,
  onUpdate: (score: number | null) => Promise<void>,
) => {
  const [isEdit, setIsEdit] = useState(false);
  const [editValue, setEditValue] = useState<string>('');
  const [value, setValue] = useState<string>(initialValue?.toString() ?? '');
  const initialValueRef = useRef<number | null>(null);

  const enterEditMode = useCallback(() => {
    if (focused === false) return;

    initialValueRef.current = initialValue;
    setEditValue(value?.toString() ?? '');
    setIsEdit(true);
    setTimeout(() => {
      const inputElement = inputRef.current?.querySelector('input');
      inputElement?.focus();
    }, 0);
  }, [focused, initialValue, value, inputRef]);

  const exitEditMode = useCallback(async () => {
    setIsEdit(false);

    const newValue = editValue === '' ? null : parseInt(editValue, 10);
    const hasChanged = newValue !== initialValueRef.current;

    if (hasChanged) {
      try {
        await onUpdate(newValue);
        setValue(newValue?.toString() ?? '');
      } catch (error) {
        console.error(error);
        setValue(initialValueRef.current?.toString() ?? '');
      }
    }
  }, [editValue, onUpdate]);

  const handleInputChange = useCallback((input: string) => {
    if (input === '' || /^\d*$/.test(input)) {
      setEditValue(input);
    }
  }, []);

  useEffect(() => {
    if (focused) {
      enterEditMode();
    }
  }, [focused, enterEditMode]);

  return {
    isEdit,
    editValue,
    enterEditMode,
    exitEditMode,
    handleInputChange,
    value,
  };
};
